import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageSquare,
  Check,
  Circle,
  ChevronRight,
  ExternalLink,
  Shield,
  Phone,
  FileText,
  Building2,
  Loader2,
  CheckCircle,
  AlertCircle,
  Copy,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const API = `${process.env.REACT_APP_API_URL}/api`;

const ONBOARDING_STEPS = [
  {
    id: "telnyx_account",
    title: "Create Telnyx Account",
    description: "Sign up for a Telnyx account to access WhatsApp Business API",
    icon: Building2,
    helpUrl: "https://telnyx.com/sign-up"
  },
  {
    id: "meta_verification",
    title: "Complete Meta Business Verification",
    description: "Verify your business with Meta to use WhatsApp Business Platform",
    icon: Shield,
    helpUrl: "https://www.facebook.com/business/help/2058515294227817"
  },
  {
    id: "whatsapp_number",
    title: "Get WhatsApp-Enabled Number",
    description: "Request a WhatsApp-enabled phone number from Telnyx",
    icon: Phone,
    helpUrl: "https://support.telnyx.com/en/articles/4389089-whatsapp-business-api-getting-started"
  },
  {
    id: "template_approval",
    title: "Submit Message Templates",
    description: "Create and submit message templates for Meta approval",
    icon: FileText,
    helpUrl: "https://developers.facebook.com/docs/whatsapp/message-templates"
  },
  {
    id: "configure_speakr",
    title: "Configure Speakr Integration",
    description: "Add your Telnyx credentials and enable WhatsApp features",
    icon: MessageSquare,
    helpUrl: null
  }
];

export default function WhatsAppOnboarding() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Configuration form
  const [config, setConfig] = useState({
    notification_number: "",
    summaries_enabled: false
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/whatsapp/status`, { headers: getAuthHeader() });
      setWhatsappStatus(res.data);
      
      // Determine completed steps based on platform config
      const completed = [];
      if (res.data.platform_configured) {
        completed.push("telnyx_account", "meta_verification", "whatsapp_number", "template_approval", "configure_speakr");
      }
      setCompletedSteps(completed);
      
      // Set form values
      setConfig({
        notification_number: res.data.business_settings?.notification_number || "",
        summaries_enabled: res.data.business_settings?.summaries_enabled || false
      });
    } catch (error) {
      console.error("Failed to fetch WhatsApp status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.notification_number) {
      toast.error("Please enter your WhatsApp number");
      return;
    }
    
    if (!config.notification_number.startsWith("+")) {
      toast.error("Phone number must be in E.164 format (e.g., +1234567890)");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/whatsapp/settings`, {
        whatsapp_summaries_enabled: true,
        whatsapp_chatbot_enabled: false,
        whatsapp_notification_number: config.notification_number
      }, { headers: getAuthHeader() });
      
      toast.success("WhatsApp configuration saved!");
      navigate("/dashboard/channels");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const markStepComplete = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
    // Move to next step
    const currentIndex = ONBOARDING_STEPS.findIndex(s => s.id === stepId);
    if (currentIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentIndex + 1);
    }
  };

  const progress = (completedSteps.length / ONBOARDING_STEPS.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="whatsapp-onboarding">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
          <MessageSquare className="w-8 h-8 text-emerald-400" />
        </div>
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
          WhatsApp Setup Wizard
        </h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Follow these steps to enable WhatsApp notifications and AI chatbot for your business
        </p>
      </div>

      {/* Progress */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Setup Progress</span>
            <span className="text-sm text-muted-foreground">
              {completedSteps.length} of {ONBOARDING_STEPS.length} steps
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>

      {/* Already Configured Alert */}
      {whatsappStatus?.platform_configured && (
        <Alert className="mb-8 border-emerald-500/30 bg-emerald-500/5">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
          <AlertTitle>WhatsApp is configured!</AlertTitle>
          <AlertDescription>
            Your platform has WhatsApp enabled. Configure your notification settings below.
          </AlertDescription>
        </Alert>
      )}

      {/* Steps */}
      <div className="space-y-4 mb-8">
        {ONBOARDING_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = completedSteps.includes(step.id);
          const isCurrent = index === currentStep;
          
          return (
            <Card 
              key={step.id}
              className={`transition-all ${isCurrent ? "ring-2 ring-primary" : ""} ${isCompleted ? "opacity-75" : ""}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Step Number/Check */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isCompleted 
                      ? "bg-emerald-500 text-white" 
                      : isCurrent 
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                  }`}>
                    {isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-bold">{index + 1}</span>
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">{step.title}</h3>
                      {isCompleted && (
                        <Badge className="bg-emerald-500">Complete</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {step.description}
                    </p>
                    
                    {/* Step Actions */}
                    {!isCompleted && (
                      <div className="flex items-center gap-2">
                        {step.helpUrl && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(step.helpUrl, "_blank")}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Guide
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          onClick={() => markStepComplete(step.id)}
                        >
                          Mark as Done
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Expanded Content for Current Step */}
                {isCurrent && !isCompleted && (
                  <div className="mt-4 pt-4 border-t">
                    {step.id === "telnyx_account" && (
                      <div className="space-y-3 text-sm">
                        <p><strong>What you'll need:</strong></p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          <li>Business email address</li>
                          <li>Business name and address</li>
                          <li>Valid payment method</li>
                        </ul>
                        <p className="text-muted-foreground">
                          Telnyx offers a developer portal with free credits to get started.
                        </p>
                      </div>
                    )}
                    
                    {step.id === "meta_verification" && (
                      <div className="space-y-3 text-sm">
                        <p><strong>Requirements:</strong></p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          <li>Facebook Business Manager account</li>
                          <li>Official business documents (tax ID, incorporation docs)</li>
                          <li>Business website with matching domain</li>
                        </ul>
                        <Alert className="mt-3">
                          <AlertCircle className="w-4 h-4" />
                          <AlertDescription>
                            Verification typically takes 2-5 business days
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                    
                    {step.id === "whatsapp_number" && (
                      <div className="space-y-3 text-sm">
                        <p><strong>Steps:</strong></p>
                        <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                          <li>Log into Telnyx Mission Control</li>
                          <li>Go to Messaging → WhatsApp</li>
                          <li>Request a new WhatsApp-enabled number</li>
                          <li>Complete the display name verification</li>
                        </ol>
                      </div>
                    )}
                    
                    {step.id === "template_approval" && (
                      <div className="space-y-3 text-sm">
                        <p><strong>Recommended Templates:</strong></p>
                        <div className="space-y-2">
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="font-medium">call_summary</p>
                            <p className="text-xs text-muted-foreground">
                              "Hi! This is a call summary from {'{{1}}'}. Agent {'{{2}}'} spoke with a customer for {'{{3}}'}. Summary: {'{{4}}'}"
                            </p>
                          </div>
                          <div className="p-3 bg-muted/50 rounded-lg">
                            <p className="font-medium">appointment_reminder</p>
                            <p className="text-xs text-muted-foreground">
                              "Reminder: You have an appointment with {'{{1}}'} on {'{{2}}'} at {'{{3}}'}."
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {step.id === "configure_speakr" && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Your WhatsApp Number</Label>
                          <Input
                            placeholder="+1234567890"
                            value={config.notification_number}
                            onChange={(e) => setConfig({...config, notification_number: e.target.value})}
                          />
                          <p className="text-xs text-muted-foreground">
                            Enter the WhatsApp number where you want to receive notifications
                          </p>
                        </div>
                        
                        <Button onClick={handleSaveConfig} disabled={saving}>
                          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Enable WhatsApp
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
          <CardDescription>
            Common questions about WhatsApp Business setup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible>
            <AccordionItem value="cost">
              <AccordionTrigger>What does it cost?</AccordionTrigger>
              <AccordionContent>
                WhatsApp Business API pricing depends on your Telnyx plan and message volume. 
                Business-initiated conversations (like call summaries) typically cost $0.005-0.05 per message 
                depending on the recipient's country.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="time">
              <AccordionTrigger>How long does setup take?</AccordionTrigger>
              <AccordionContent>
                The technical setup can be done in a few hours. However, Meta Business verification 
                typically takes 2-5 business days, and template approval can take 24-48 hours.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="templates">
              <AccordionTrigger>Why do I need message templates?</AccordionTrigger>
              <AccordionContent>
                WhatsApp requires pre-approved templates for business-initiated messages. This is to 
                prevent spam and ensure users receive valuable, expected communications. Templates 
                are reviewed by Meta for compliance.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="support">
              <AccordionTrigger>Can Speakr help with setup?</AccordionTrigger>
              <AccordionContent>
                Yes! Contact our support team at info@gskproductions.ca and we can guide you through 
                the setup process or help troubleshoot any issues.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Skip Option */}
      <div className="text-center mt-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/dashboard/channels")}
        >
          Skip for now - I'll set this up later
        </Button>
      </div>
    </div>
  );
}
